import { useState } from "react";
import { Ban, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useSafety } from "@/lib/safety/useSafety";
import { toast } from "sonner";

interface Props {
  targetUserId: string;
  targetName?: string;
  trigger?: React.ReactNode;
  onBlocked?: () => void;
  /** Optional receipt context — last DM seen + originating report id. */
  lastMessageId?: string;
  lastMessageExcerpt?: string;
  reportId?: string;
}

const BlockButton = ({
  targetUserId,
  targetName,
  trigger,
  onBlocked,
  lastMessageId,
  lastMessageExcerpt,
  reportId,
}: Props) => {
  const { blockUser } = useSafety();
  const [busy, setBusy] = useState(false);

  const handleBlock = async () => {
    setBusy(true);
    const { error } = await blockUser(targetUserId, {
      lastMessageId,
      lastMessageExcerpt,
      reportId,
    });
    setBusy(false);
    if (error) {
      toast.error(error);
      return;
    }
    toast.success(`${targetName ?? "Member"} blocked. They won't see you and you won't see them.`);
    onBlocked?.();
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        {trigger ?? (
          <Button variant="outline" size="sm">
            <Ban className="h-4 w-4 mr-1" /> Block
          </Button>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Block {targetName ?? "this member"}?</AlertDialogTitle>
          <AlertDialogDescription>
            They won't see your profile, send you messages, or appear in your discovery.
            Any existing match will be archived. You can unblock from your Account anytime.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleBlock} disabled={busy}>
            {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Block
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default BlockButton;
